/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService, SharedFormService } from "../../../../services";
import { MerchantDetailsParentComponent } from "../../merchant-details-parent.component";
import { Merchant } from "../../../../models";
import { minSelected } from "../../../../validators/MPCustomValidator";
import { PATTERN_CONSTANTS } from "../../../../utils/data.rest.constants";

@Component({
  selector: "app-not-wp-merchant-details",
  templateUrl: "./payPal-qbpay-merchant-details.component.html"
})
export class PayPalQbpayMerchantDetailsComponent extends MerchantDetailsParentComponent implements OnInit {
  merchantDetailsForm!: FormGroup;
  editable = false;
  actionInProgress = false;
  hasRemit = false;
  buttonName = "Update Merchant Details";
  @Input("loaded") loaded = false;
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private sharedFormService: SharedFormService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.merchantDetailsForm = this.fb.group({
      generalInfo: this.fb.group({
        merchantId: [{ value: this.merchant.id, disabled: true }],
        merchantName: [this.merchant.merchantName, [Validators.required, Validators.maxLength(50)]],
        doingBusinessAs: [this.merchant.doingBusinessAs, [Validators.maxLength(99)]],
        alsoKnownAsName: [this.merchant.alsoKnownAsName],
        externalMerchantId: [this.merchant.externalMerchantId, [Validators.maxLength(12)]],
        customerAccountNumber: [this.merchant.customerAccountNumber, Validators.required],
        phoneNumber: [this.merchant.phoneNumber, [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10)]],
        maxTransactionLimit: [
          this.merchant.maxTransactionLimit / 100,
          [Validators.pattern(PATTERN_CONSTANTS.MONEY), Validators.required, Validators.min(0)]
        ]
      }),
      contacts: this.sharedFormService.getContactsForm(this.merchant),
      merchantAddress: this.sharedFormService.getMerchantAddressFormGroup(this.merchant),
      legalEntity: this.sharedFormService.getLegalEntity(this.merchant),
      acceptedCards: this.fb.group({}, { validators: minSelected(1) })
    });
    if (this.merchant.remitToAddress) {
      this.merchantDetailsForm.setControl(
        "remitToAddress",
        this.sharedFormService.getRemitAddressFormGroup(this.merchant)
      );
    }
    this.sharedFormService.addCheckboxes(this.acceptedCardsFormGroup, this.merchant);
  }

  updateGeneralInfo() {
    if (this.merchantDetailsForm.valid) {
      this.actionInProgress = true;
      this.setFeeDetailsToNullIfNoneExist(this.merchant);
      const tempMerchant: Merchant = JSON.parse(JSON.stringify(this.merchant));
      this.sharedFormService.updateMerchantGeneralInfo(tempMerchant, this.generalInfoFormGroup);
      this.sharedFormService.updateCustomerContact(tempMerchant, this.contactsFormGroup);
      if (!this.hasRemit) {
        this.sharedFormService.updateMerchantAddress(tempMerchant, this.merchantAddressFormGroup);
      } else {
        this.sharedFormService.updateMerchantAddress(
          tempMerchant,
          this.merchantAddressFormGroup,
          this.remitAddressFormGroup
        );
      }
      this.sharedFormService.updateLegalEntity(tempMerchant, this.legalEntityFormGroup);
      this.sharedFormService.updatedAcceptedCreditCards(tempMerchant, this.acceptedCardsFormGroup);
      tempMerchant.contractedRates = null as any;
      this.merchantService.updateMerchantGeneralInfo(tempMerchant).subscribe(
        (merchant) => {
          this.merchant = merchant;
          this.editable = false;
          this.actionInProgress = false;
        },
        (error) => {
          this.notificationService.error(error.error);
          this.actionInProgress = false;
        }
      );
    } else {
      this.merchantDetailsForm.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  setEditable(value: boolean) {
    this.editable = value;
    this.merchantDetailsForm.markAllAsTouched();
  }

  hasRemitEvent(hasRemitEvent: boolean) {
    this.hasRemit = hasRemitEvent;
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
  }

  get generalInfoFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["generalInfo"] as FormGroup;
  }

  get contactsFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["contacts"] as FormGroup;
  }

  get merchantAddressFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["merchantAddress"] as FormGroup;
  }

  get remitAddressFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["remitToAddress"] as FormGroup;
  }

  get legalEntityFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["legalEntity"] as FormGroup;
  }

  get acceptedCardsFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["acceptedCards"] as FormGroup;
  }
}
